<template>
	<div
		class="steps__step"
		:class="( is_completed_step ) ? ' steps__step--completed' : ( is_current_step ) ? ' steps__step--active' : null"
	>
		<div class="steps__step-number">
			{{ step.step_number }}
		</div>
		<div class="steps__step-title-wrapper">
			<h2 class="steps__step-title">
				{{ step.title }}
			</h2>
			<template v-if="is_completed_step">
				<ul class="steps__step-summary">
					<li v-if="form_fields.billing_location">
						Billing Location:
						<span>{{ getBillingLocationLabel( form_fields.billing_location ) }}</span>
					</li>
					<li v-if="form_fields.production_office">
						Production Office:
						<span>{{ getOfficeLabel( form_fields.production_office ) }}</span>
					</li>
					<li v-if="form_fields.currency">
						Currency:
						<span>{{ getCurrencyLabel( form_fields.currency ) }}</span>
					</li>
					<li v-if="total">
						Total project cost:
						<span>{{ total }}</span>
					</li>
				</ul>
			</template>
		</div>
		<button
			v-if="is_completed_step"
			class="button button--smaller"
			type="button"
			@click="setActiveStep( step.step_number )"
		>
			Edit
		</button>
		<template v-else-if="is_current_step">
			<ProjectScopeFormComponent
				:step-title="step.title"
			/>
		</template>
	</div>
</template>

<script setup>
import { useNewProjectStore } from '../../stores/new-project.js';
import { computed, defineProps, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { formatCurrency } from '../../../helpers';
import ProjectScopeFormComponent from './ProjectScopeFormComponent';

const new_project_store = useNewProjectStore();
const { setActiveStep, getBillingLocations, getOffices, getCurrencies } = new_project_store;
const {
	form_fields,
	billing_locations,
	offices,
	currencies
} = storeToRefs( new_project_store );

const props = defineProps( {
	step: {
		required: true,
		type: Object
	},
	activeStepNumber: {
		required: true,
		type: Number
	}
} );

const is_current_step = computed( () => {
	return props.step.step_number === props.activeStepNumber;
} );

const is_completed_step = computed( () => {
	return props.step.step_number < props.activeStepNumber;
} );

function getBillingLocationLabel( object_value ) {
	const item = billing_locations.value.find( ( { value } ) => parseInt( value ) === parseInt( object_value ) );
	if ( !item || !item.label ) {
		return;
	}
	return item.label;
}

function getOfficeLabel( object_value ) {
	const item = offices.value.find( ( { value } ) => parseInt( value ) === parseInt( object_value ) );
	if ( !item || !item.label ) {
		return;
	}
	return item.label;
}

function getCurrencyLabel( object_value ) {
	const item = currencies.value.find( ( { value } ) => parseInt( value ) === parseInt( object_value ) );
	if ( !item || !item.label ) {
		return;
	}
	return item.label;
}

const selected_currency = computed( () => {
	const selected = currencies.value.find( currency => parseInt( currency.value ) === parseInt( form_fields.value.currency ) );
	return selected ?? { label: 'USD' };
} );

const total = computed( () => {
	if ( !form_fields.value.project_scope || !form_fields.value.project_scope.items?.length ) {
		return null;
	}
	const total_cost = form_fields.value.project_scope.items.reduce( ( acc, val ) => acc + val.total, 0 );
	return formatCurrency( total_cost, selected_currency.value.label )
} );

onMounted ( async() => {
	await getBillingLocations();
	await getOffices();
	await getCurrencies();
} );

</script>
